import { Phone, Video, Search, MoreVertical, X, Info, Pin, CheckSquare } from "lucide-react"
import type { Conversation } from "./types"
import { useState } from "react"
import { useTranslation } from "@/clientWeb/i18n/LanguageContext"
import { useConversationStatus, useConversationLastSeen, useConversationCustomMessage, useConversationHidden } from "@/features/presence/hooks/useConversationOnline"
import { statusDotClass, statusTextClass, statusLabelKey } from "@/features/presence/utils/statusMeta"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/clientWeb/components/ui/dropdown-menu"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/clientWeb/components/ui/dialog"
import { Button } from "@/clientWeb/components/ui/button"
import { Checkbox } from "@/clientWeb/components/ui/checkbox"
import { toast } from "@/clientWeb/components/ui/toast-utils"
import { AvatarPhoto } from "./avatar-photo"
import { useMessengerStore } from "./store/messenger.store"
import { useAuthStore } from "@/features/auth/store/auth.store"

interface ConversationHeaderProps {
  conversation: Conversation
  onClose?: () => void
  onOpenInfo?: () => void
  onStartAudioCall?: () => void
  onStartVideoCall?: () => void
  pinnedCount?: number
  onOpenPinned?: () => void
  onOpenStarred?: () => void
  onSearchChange?: (query: string) => void
  isMuted?: boolean
  onMuteChatToggle?: () => void
  /**
   * Real messages in the thread — system notices excluded, since nobody sent
   * them and neither action can touch them.
   *
   * Gates the two menu items that need something to act on. "Clear chat" on an
   * empty thread opened a confirmation dialog asking to delete nothing, and
   * confirming it did nothing, so it greys out; "Select messages" entered a
   * selection mode with nothing selectable and no way to select anything, so it
   * drops out of the menu entirely (with its separator, or an empty thread
   * would open on a stray divider).
   */
  messageCount?: number
  /**
   * Both carry the confirm dialog's "Keep starred messages" choice, because the
   * server needs it at the moment of the clear: with it off the starred rows go
   * with the messages, and with it on the kept set is snapshotted then and there.
   */
  onClearMessages?: (opts: { keepStarred: boolean }) => void
  onDeleteChat?: (opts: { keepStarred: boolean }) => void
  /** Really leave the group, dropping my participant row. Group-only; never
   *  offered on a DM or a broadcast. */
  onExitGroup?: () => void
  onBlockContact?: () => void
  /** True when this contact is already blocked. Without it the menu offered
   *  "Block contact" to somebody who was already blocked — the one action that
   *  could not do anything, in place of the one they wanted. */
  isBlocked?: boolean
  onUnblockContact?: () => void
  onEnterSelectMode?: () => void
}

function Avatar({ name, imageUrl, contactId, size = 36 }: { name: string; imageUrl?: string; contactId?: string; size?: number }) {
  const initials = name.split(" ").map(w => w[0]).slice(0, 2).join("").toUpperCase()
  const colors = ["bg-primary/20 text-primary", "bg-violet-500/20 text-violet-600", "bg-emerald-500/20 text-emerald-600", "bg-amber-500/20 text-amber-600"]
  const colorIdx = name.charCodeAt(0) % colors.length
  return (
    <div
      className={`rounded-full flex items-center justify-center text-xs font-semibold shrink-0 overflow-hidden ${colors[colorIdx]}`}
      style={{ width: size, height: size }}
    >
      <AvatarPhoto src={imageUrl} alt={name} className="w-full h-full object-cover" fallback={initials} contactId={contactId} />
    </div>
  )
}

export function ConversationHeader({
  conversation,
  onClose,
  onOpenInfo,
  onStartAudioCall,
  onStartVideoCall,
  pinnedCount = 0,
  onOpenPinned,
  onOpenStarred,
  onSearchChange,
  isMuted,
  onMuteChatToggle,
  messageCount = 0,
  onClearMessages,
  onDeleteChat,
  onExitGroup,
  onBlockContact,
  isBlocked = false,
  onUnblockContact,
  onEnterSelectMode,
}: ConversationHeaderProps) {
  const t = useTranslation()
  const c = conversation
  const [searching, setSearching] = useState(false)
  const [searchText, setSearchText] = useState("")
  const [confirmType, setConfirmType] = useState<null | "clear" | "block" | "delete" | "exit">(null)
  // Checked by default, like WhatsApp's dialog — losing bookmarks should be the
  // deliberate choice, not the one you get by not reading the dialog. Re-armed
  // on every open so an earlier uncheck doesn't quietly persist.
  const [keepStarred, setKeepStarred] = useState(true)

  const isGroup = c.isGroup
  const myUserId = useAuthStore((s) => s.presenceId)
  // The other person in a DM, so the header can ask the directory for their
  // CURRENT picture rather than trust `c.imageUrl` (a copy taken when the
  // conversation was created). No single peer on a group.
  const peerId = !isGroup ? c.participants?.find(p => p.id !== myUserId)?.id : undefined
  /**
   * A broadcast list is neither a group nor a 1:1 chat.
   *
   * `isGroup` is falsy on one, so every branch below used to fall through to
   * the DM copy: the menu offered "Delete chat" over something that is a list
   * of recipients, and offered to block a contact that does not exist.
   */
  const isBroadcast = !!c.isBroadcast

  // Delete chat, on a group as much as on a DM.
  //
  // This used to say "Exit group" for a group, on the belief that deleting a
  // group chat dropped your participant row. It does not: `DELETE
  // /conversations/:id` runs `leave()`, which is a straight call to `hide()` —
  // it sets `hidden_at` on your own participant row and clears your copy of
  // the history. You stay a member, and the thread comes back the moment
  // somebody posts. Leaving for real is a different endpoint entirely
  // (`DELETE /conversations/:id/members/:me`).
  //
  // So the label promised something that never happened. Someone who wanted
  // their list tidied was warned they were about to leave the group, and
  // someone who actually wanted out was told they had left when they had not.
  // A group I have left stays here read-only; deleting it is the one action
  // left, and it removes the group from MY chats only.
  const hasLeftGroup = useMessengerStore((s) => s.removedFromConversationIds.has(c.id))
  const deleteLabel = isBroadcast ? 'Delete broadcast' : hasLeftGroup ? t('presence.deleteGroup') : t('presence.deleteChat')

  const dialogTitle =
    confirmType === "clear" ? t('presence.clearChat') :
    confirmType === "block" ? t('presence.blockContact') :
    confirmType === "delete" ? deleteLabel :
    confirmType === "exit" ? t('presence.exitGroup') :
    ""

  const dialogDescription =
    confirmType === "clear"
      ? t('presence.clearChatDesc')
      : confirmType === "block"
        ? t('presence.blockContactDesc')
        : confirmType === "delete"
          ? (isBroadcast
              ? `${c.name} and its recipient list will be deleted. Messages already sent are not recalled.`
              : hasLeftGroup
                ? t('presence.deleteGroupDescWithName', { name: c.name })
                : t('presence.deleteChatDesc'))
          : confirmType === "exit"
            ? t('presence.exitGroupDescWithName', { name: c.name })
            : ""

  const primaryLabel =
    confirmType === "clear" ? t('common.clear') :
    confirmType === "block" ? t('common.block') :
    confirmType === "delete" ? t('common.delete') :
    confirmType === "exit" ? t('common.exit') :
    t('common.confirm')

  const handleConfirm = () => {
    if (!confirmType) return
    if (confirmType === "clear") {
      onClearMessages?.({ keepStarred })
      toast.success({ title: t('presence.chatCleared'), description: t('presence.chatClearedDesc') })
    } else if (confirmType === "block") {
      onBlockContact?.()
      toast.info({ title: t('presence.contactBlocked'), description: t('presence.blockedContactDesc', { name: c.name }) })
    } else if (confirmType === "delete") {
      onDeleteChat?.({ keepStarred })
      /**
       * Past tense, and not the dialog's wording.
       *
       * This reused `deleteLabel`, which is the CONFIRMATION title — "Delete
       * chat?" for a DM, "Exit group?" for a group. As a toast heading that
       * asks a question about something already done, and paired with a
       * description in the past tense it read as
       * "Delete chat? — Varun has been deleted", which is either a prompt or a
       * receipt and cannot be both.
       *
       * `chatDeleted` and `leftGroup` already existed for this and had never
       * been wired up.
       */
      const leaving = isGroup && !isBroadcast && !hasLeftGroup
      toast.success({
        title: isBroadcast
          ? 'Broadcast deleted'
          : leaving ? t('presence.leftGroup') : t('presence.chatDeleted'),
        // "Shoyab has been deleted" describes deleting a PERSON. The thing that
        // was deleted is the conversation, so the sentence has to say so — the
        // name identifies which chat, it is not the object of the verb.
        description: leaving
          ? `You have left ${c.name}.`
          : isBroadcast
            ? t('presence.broadcastDeletedDesc', { name: c.name })
            : t('presence.chatDeletedWith', { name: c.name }),
      })
    } else if (confirmType === "exit") {
      // No toast here: exiting can fail (already removed, server error), and
      // only the caller knows once the request has actually resolved.
      onExitGroup?.()
    }
    setConfirmType(null)
  }

  // Counted from the wire conversation, which is what the group actions and
  // the socket keep current. This used to read `c.participants` — the UI-side
  // copy, written when the list was built and never again — so adding members
  // (or anyone arriving on an invite link) left the header saying the old
  // number until a reload. Its `?? 3` fallback was worse than no number: a
  // group whose participants had not loaded reported three members, and three
  // is a real enough count that nobody would question it.
  const wireParticipantCount = useMessengerStore(
    (st) => st.wireConversations.get(c.id)?.participants?.length,
  )
  const memberCount = wireParticipantCount ?? c.participants?.length ?? 0

  const peerStatus = useConversationStatus(c)
  const lastSeen = useConversationLastSeen(c)
  const peerCustomMessage = useConversationCustomMessage(c)
  const peerHidden = useConversationHidden(c)
  // A contact who turned status visibility off gets no dot and no label at
  // all, not even "Offline", rather than the ordinary offline rendering.
  const dotClass = !c.isGroup && peerHidden ? undefined : statusDotClass[peerStatus]
  const presenceLabel = c.isGroup
    ? `${memberCount} ${t('presence.members')}`
    : peerHidden
      ? ''
      : peerStatus !== 'offline'
        ? t(statusLabelKey[peerStatus])
        : lastSeen
          ? t('presence.lastSeenAt', { time: lastSeen })
          // No time known and not hidden: genuinely offline with no
          // last-seen on record.
          : t(statusLabelKey.offline)
  // The peer's custom status rides alongside the presence label rather than
  // replacing it — "Online" and "In a meeting until 4" answer different
  // questions, and dropping either one loses information the header had.
  // Hidden drops the custom message too: it travels in the same meta the
  // contact just asked not to share.
  const subtitle =
    !c.isGroup && peerHidden
      ? ''
      : peerCustomMessage
        ? `${presenceLabel} · ${peerCustomMessage}`
        : presenceLabel

  return (
    <div className="bg-card border-b border-border shrink-0 shadow-sm">
    <div className="flex items-center gap-3 px-4 py-3">
      {/* Close button (desktop embedded) */}
      {onClose && (
        <button onClick={onClose} className="p-1.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
          <X className="w-4 h-4" />
        </button>
      )}

      {/* Avatar + name - clickable for profile */}
      <button
        onClick={onOpenInfo}
        className="flex items-center gap-3 flex-1 min-w-0 text-left hover:opacity-80 transition-opacity"
      >
        <div className="relative">
          <Avatar name={c.name} imageUrl={c.imageUrl} contactId={peerId} />
          {dotClass && !c.isGroup && (
            <span className={`absolute bottom-0 right-0 w-2.5 h-2.5 rounded-full ${dotClass} border-2 border-background`} />
          )}
        </div>
        <div className="min-w-0">
          {/* Same reason as the list row: names are allowed up to 100
              characters and this line cannot show that many. */}
          <p className="text-sm font-semibold text-foreground truncate" title={c.name}>{c.name}</p>
          <p className={`text-xs truncate ${peerStatus !== 'offline' && !c.isGroup ? statusTextClass[peerStatus] : "text-muted-foreground"}`}>
            {subtitle}
          </p>
        </div>
      </button>

      {/* Actions */}
      {searching ? (
        <div className="flex items-center gap-1 flex-1 max-w-xs">
          <input
            autoFocus
            value={searchText}
            onChange={e => { setSearchText(e.target.value); onSearchChange?.(e.target.value) }}
            placeholder={t('presence.searchInChat')}
            className="flex-1 text-sm bg-muted rounded-lg px-3 py-1.5 outline-none text-foreground placeholder:text-muted-foreground"
          />
          <button onClick={() => { setSearching(false); setSearchText(""); onSearchChange?.("") }} className="p-1.5 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      ) : (
        <div className="flex items-center gap-0.5">
          {/* A broadcast is a fan-out to many recipients, not a conversation
              with someone, so there is nobody to call. Offering the action
              invites the reader to expect it to work. */}
          {!isBroadcast && (
            <>
              <button
                className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title={t('presence.voiceCall')}
                type="button"
                onClick={onStartAudioCall}
              >
                <Phone className="w-4.5 h-4.5" />
              </button>
              <button
                className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors"
                title={t('presence.videoCall')}
                type="button"
                onClick={onStartVideoCall}
              >
                <Video className="w-4.5 h-4.5" />
              </button>
            </>
          )}
          <button onClick={() => setSearching(true)} className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title={t('common.search')}>
            <Search className="w-4.5 h-4.5" />
          </button>
          <button onClick={onOpenInfo} className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors" title={t('presence.info')}>
            <Info className="w-4.5 h-4.5" />
          </button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="p-2 rounded-full hover:bg-muted text-muted-foreground hover:text-foreground transition-colors">
                <MoreVertical className="w-4.5 h-4.5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {messageCount > 0 && (
                <>
                  <DropdownMenuItem onClick={onEnterSelectMode}>
                    <CheckSquare className="w-4 h-4 mr-2" />
                    Select messages
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={() => onMuteChatToggle?.()}>
                {isMuted ? t('presence.unmuteNotifications') : t('presence.muteNotifications')}
              </DropdownMenuItem>
              <DropdownMenuItem onClick={onOpenStarred}>{t('presence.starredMessages')}</DropdownMenuItem>
              <DropdownMenuItem
                // Clearing needs membership; a group I left is deleted instead.
                disabled={messageCount === 0 || hasLeftGroup}
                onClick={() => setConfirmType("clear")}
              >
                {t('presence.clearChatAction')}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {!c.isGroup && !isBroadcast && (
                isBlocked ? (
                  // No confirmation: unblocking restores the ordinary state and
                  // is undone by blocking again. Only the destructive direction
                  // asks.
                  <DropdownMenuItem onClick={() => onUnblockContact?.()}>
                    {t('presence.unblock')}
                  </DropdownMenuItem>
                ) : (
                  <DropdownMenuItem onClick={() => setConfirmType("block")}>
                    {t('presence.blockContactAction')}
                  </DropdownMenuItem>
                )
              )}
              {/* Delete hides the thread for you and clears your copy. Not
                  offered on a group: it would leave you a member with the group
                  hidden until someone posts. A group is cleared or exited. */}
              {(!isGroup || isBroadcast || hasLeftGroup) && (
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => setConfirmType("delete")}
                >
                  {isBroadcast ? 'Delete broadcast' : hasLeftGroup ? t('presence.deleteGroupAction') : t('presence.deleteChatAction')}
                </DropdownMenuItem>
              )}
              {/* The real leave: a group can end its membership, a DM or
                  broadcast cannot. */}
              {isGroup && !isBroadcast && !hasLeftGroup && (
                <DropdownMenuItem
                  className="text-destructive"
                  onClick={() => setConfirmType("exit")}
                >
                  {t('presence.exitGroupAction')}
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      )}
    </div>
    {/* Confirm dialog for clear / block / delete */}
    <Dialog
      open={!!confirmType}
      onOpenChange={open => {
        if (!open) setConfirmType(null)
        else setKeepStarred(true)
      }}
    >
      <DialogContent className="max-w-sm">
        <DialogHeader>
          <DialogTitle>{dialogTitle}</DialogTitle>
          {dialogDescription && (
            <DialogDescription>{dialogDescription}</DialogDescription>
          )}
        </DialogHeader>
        {/* Blocking doesn't touch messages, so the choice would be meaningless there. */}
        {(confirmType === "clear" || confirmType === "delete") && (
          <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
            <Checkbox
              checked={keepStarred}
              onCheckedChange={(v) => setKeepStarred(v === true)}
            />
            {t('presence.keepStarredMessages')}
          </label>
        )}
        <DialogFooter className="pt-2">
          <Button variant="outline" onClick={() => setConfirmType(null)}>
            {t('common.cancel')}
          </Button>
          <Button variant="destructive" onClick={handleConfirm}>
            {primaryLabel}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>

    {/* Pinned messages banner */}
    {pinnedCount > 0 && (
      <button
        onClick={onOpenPinned}
        className="w-full flex items-center gap-2 px-4 py-1.5 bg-primary/5 hover:bg-primary/10 transition-colors border-b border-border/50"
      >
        <Pin className="w-3.5 h-3.5 text-primary shrink-0" />
        <span className="text-xs text-primary dark:text-foreground font-medium flex-1 text-left">
          {pinnedCount} {t('presence.pinned')} {pinnedCount === 1 ? t('presence.message') : t('presence.messages')}
        </span>
        <span className="text-xs text-primary dark:text-foreground">{t('presence.viewAll')}</span>
      </button>
    )}
    </div>
  )
}
